import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../App';
import { Task, TaskType } from 'konect-model';
import "./TaskEditor.css";
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit';
import UserControlService from '../../services/user-control-service';
import { nanoid } from 'nanoid';

interface TaskEditorNodeProps {
    task: Task;
    props: any;
}

const TaskEditorNode = ({task, props}: TaskEditorNodeProps) => {
    const { project, setProject } = useContext(AppContext);
    const [scrollProgress, setScrollProgress] = useState({left: 0, top: 0});

    const [active, setActive] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [keepContextMenuOpen, setKeepContextMenuOpen] = useState<boolean>(false);
    const [contextMenuOffset, setContextMenuOffset] = useState<number>(50);
    const [showMoveMenu, setShowMoveMenu] = useState<boolean>(false);
    const [showSiblingMenu, setShowSiblingMenu] = useState<boolean>(false);
    const [siblingTasks, setSiblingTasks] = useState<Task[]>([]);
    const graphCellRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.persistentContextMenu === task.id) setKeepContextMenuOpen(true);
        else setKeepContextMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.persistentContextMenu])

    useEffect(() => {
        if (keepContextMenuOpen) props.setPersistentContextMenu(task.id);
        else props.setPersistentContextMenu("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keepContextMenuOpen])

    /* HELPER FUNCTIONS */

    // Helper-Function to recursively delete a task and its children from the taskModel and the ideaBox
    const deleteTask = (task: Task|undefined) => {
        if (task) {
            if (task.subtasks && task.subtasks.length > 0){
                for (const subtask of task.subtasks) {
                    deleteTask(subtask);
                }
            }
            if (task.parent) {
                const parentTask = project.taskModel.rootTask.findTask(task.parent);
                if (parentTask) {
                    parentTask.subtasks = parentTask.subtasks.filter(t => t.id !== task.id);
                    project.ideaBox.entries = project.ideaBox.entries.filter((entry) => entry.task.id !== task.id); //delete entries from IdeaBox
                }
            }
        } else {
            UserControlService.showInfoPopup({ title: "Deletion Failed", content: `Couldn't find task to be deleted.`, duration: 5000, color: "danger" });
        }
    }

    // Function to dis- or enable the arrow buttons of the move menu
    const disableMoveButton = (direction: String) => {
        const parent = project.taskModel.rootTask.findTask(task.parent);
        if (!parent) return true;
        const index = parent.subtasks.findIndex(t => t.id === task.id);
        if (direction === "right" && index < parent.subtasks.length - 1) return false;
        if (direction === "left" && index >= 1) return false;
        const grandparent = project.taskModel.rootTask.findTask(parent.parent);
        if (direction === "up" && grandparent) return false;
        if (direction === "down" && parent.subtasks.length > 1) return false;
        return true;
    }

    // This is executed when clicking the ARROW Buttons and moves the selected cell left/right within its hierarchy level or up/down the hierarchy
    const moveCell = (direction: string, taskId?: string) => {
        UserControlService.update();
        const parent = project.taskModel.rootTask.findTask(task.parent);
        if (!parent) return;
        const grandparent = project.taskModel.rootTask.findTask(parent.parent);
        const index = parent.subtasks.findIndex(t => t.id === task.id);
        if (direction === "right" && index < parent.subtasks.length - 1) {
            parent.subtasks[index] = parent.subtasks.splice(index + 1, 1, task)[0];
        } else if (direction === "left" && index >= 1) {
            parent.subtasks[index] = parent.subtasks.splice(index - 1, 1, task)[0];
        } else if (direction === "up" && grandparent) {
            parent.subtasks.splice(index, 1);
            grandparent.subtasks.push(task);
            task.parent = grandparent.id;
        } else if (direction === "down" && taskId && parent.subtasks.length > 1) {
            const newParent = project.taskModel.rootTask.findTask(taskId);
            if (!newParent) return;
            parent.subtasks.splice(index, 1);
            newParent.subtasks.push(task);
            task.parent = taskId;
        }
        setSiblingTasks([]);
        setShowMoveMenu(false);
        setShowSiblingMenu(false);
        setProject(project);
        setTimeout(() => setActive(false), 2000)
    }

    const openMoveCellDownMenu = () => {
        const parent = project.taskModel.rootTask.findTask(task.parent);
        if (!parent) return;
        setSiblingTasks(parent.subtasks.filter((t:Task) => t.id !== task.id));
        setShowSiblingMenu(true);
    }

    const addInformationElement = (type: TaskType) => {
        UserControlService.update();
        if (!task.type.includes(type)) task.addTaskRule(project, nanoid(), "", [type]);
        setProject(project);
    }

    /* EVENT HANDLERS */

    const handleMouseLeave = () => {
        if (!keepContextMenuOpen) {
            setActive(false);
            setShowContextMenu(false);
            if (showMoveMenu) {
                setSiblingTasks([]);
                setShowMoveMenu(false);
                setShowSiblingMenu(false);
            }
        }

        if (props.persistentContextMenu !== "" && props.persistentContextMenu !== task.id) {
            const element = (document.getElementById(props.persistentContextMenu) as HTMLElement);
            if (!element) return
            const menu = element.querySelector("#context-menu") as HTMLElement;
            if (menu) menu.style.visibility = 'visible';
        }
    }

    const handleMouseEnter = () => {
        setActive(true);
        // calculate the offset of the context-menu to avoid it clipping the viewport edge
        const node = document.getElementById(task.id)?.getBoundingClientRect();
        if (!node) return;
        const container = document.getElementById('graph-container');
        if (!container) return;
        const minX = container.getBoundingClientRect().x;
        const maxX = window.innerWidth - 235;
        if (minX && node.left < minX + 100) setContextMenuOffset(25);
        else if (maxX && node.left + node.width  > maxX) setContextMenuOffset(75);
        else setContextMenuOffset(50);
        setShowContextMenu(true);

        // save scroll position
        setScrollProgress({
            top: (container.scrollTop / (container.scrollHeight - container.clientHeight)) * 100 || 0,
            left: (container.scrollLeft / (container.scrollWidth - container.clientWidth)) * 100 || 0
        });

        if (props.persistentContextMenu !== "" && props.persistentContextMenu !== task.id) {
            const element = (document.getElementById(props.persistentContextMenu) as HTMLElement);
            if (!element) return
            const menu = element.querySelector("#context-menu") as HTMLElement;
            if (menu) menu.style.visibility = 'hidden';
        }
    }

    useEffect(() => {
        const handleClick = (event :any) => {
            if (graphCellRef.current && !graphCellRef.current.contains(event.target)) {
                setKeepContextMenuOpen(false);
                setShowContextMenu(false);
            }
            else setKeepContextMenuOpen(true);
        }
        window.addEventListener('mousedown', handleClick);

        return () => {
            window.removeEventListener('mousedown', handleClick);
        }
    },[])

    /* CONTEXT MENU BUTTON HANDLERS */

    const handleAddButton = () => {
        props.handleAddButton(task);
    }

    const handleEditButton = () => {
        props.handleEditButton(task);
    }

    // const handleMoveButton = () => {
    //     setShowMoveMenu(true);
    //     setShowContextMenu(false);
    //     setActive(true);
    // }

    const handleDeleteButton = () => {
        UserControlService.update();
        deleteTask(task);
        setProject(project);
        // restore scroll position
        const container = document.getElementById("graph-container");
        if (!container) return;
        container.scrollTop = (scrollProgress.top / 100) * (container.scrollHeight - container.clientHeight);
        container.scrollLeft = (scrollProgress.left / 100) * (container.scrollWidth - container.clientWidth);
    }

    return (
    <div ref={graphCellRef} className="graphCell" id={`${task.id}`} style={{backgroundColor: active ? "#cceded" : "", pointerEvents: props.isDragging ? 'none' : 'auto'}} onMouseEnter={() => {if (!props.isDragging) handleMouseEnter()}} onMouseLeave={() => {handleMouseLeave()}} onDoubleClick={handleEditButton}>
        <span>{task.name}</span>

        {task.subtasks.length > 0 && <div className={`graphIconCollapseWrapper`} onClick={() => {props.collapseTask(task)}}>
            <div className={`graphIconCollapse`}>
                <MDBIcon fas icon="minus"/>
            </div>
        </div>}

        {!showMoveMenu && <div className="graphIconContainer">
        <div className={`graphIconBorder ${task.type.includes(TaskType.INFORMATION) ? "graphIconInformation" : "graphIconOff information" }`} onClick={() => {addInformationElement(TaskType.INFORMATION)}}>
                <MDBIcon fas icon="eye" style={{color: "#ffffff"}}/>
            </div>
            <div className={`graphIconBorder ${task.type.includes(TaskType.INTERACTION) ? "graphIconInteraction" : "graphIconOff interaction" }`} onClick={() => {addInformationElement(TaskType.INTERACTION)}}>
                <MDBIcon fas icon="hand-paper" style={{color: "#ffffff"}}/>
            </div>
        </div>}

        {/* Context Menu */}
        {showContextMenu && !showMoveMenu &&
        <div id="context-menu" style={{left: `${contextMenuOffset}%`, translate: `-${contextMenuOffset}%`}}>
            <MDBBtn className="task-button" onClick={() => {handleAddButton()}} color="primary" rounded>
                <i className="fas fa-plus"></i>
            </MDBBtn>
            <MDBBtn className="task-button" onClick={() => {handleEditButton()}} color="warning" rounded>
                <i className="fas fa-pen"></i>
            </MDBBtn>
            {/*<MDBBtn className="task-button movemenu-button" onClick={() => {handleMoveButton()}} rounded>
                <i className="fas fa-arrows-alt"></i>
        </MDBBtn>*/}
            <MDBBtn className="task-button" onClick={() => {handleDeleteButton()}} color="danger" rounded>
                <i className="fas fa-trash"></i>
            </MDBBtn>
        </div>}

        {/* Move Menu */}
        {showMoveMenu && <>
            <div className="moveMenu moveUp">
                <MDBBtn className={"moveMenuButton justify-content-center align-items-start"} onClick={() => {moveCell("up")}} disabled={disableMoveButton("up")}>
                    <i className="fas fa-caret-up"></i>
                </MDBBtn>
            </div>
            <div className="moveMenu moveRight">
                <MDBBtn className={"moveMenuButton justify-content-end align-items-center"} onClick={() => {moveCell("right")}} disabled={disableMoveButton("right")}>
                <i className="fas fa-caret-right"></i>
                </MDBBtn>
            </div>
            <div className="moveMenu moveDown">
                <MDBBtn className={"moveMenuButton justify-content-center align-items-end"} onClick={() => {openMoveCellDownMenu()}} disabled={disableMoveButton("down")}>
                    <i className="fas fa-caret-down"></i>
                </MDBBtn>
            </div>
            <div className="moveMenu moveLeft">
                <MDBBtn className={"moveMenuButton justify-content-start align-items-center"} onClick={() => {(moveCell("left"))}} disabled={disableMoveButton("left")}>
                    <i className="fas fa-caret-left"></i>
                </MDBBtn>
            </div>
        </>}

        {showSiblingMenu && <div id="sibling-list">
            {siblingTasks.map((task:Task) => {
                return <MDBBtn key={task.id} color={"light"} onClick={() => {moveCell("down", task.id)}}>{task.name}</MDBBtn>
            })}
        </div>}
    </div>)
}

export default TaskEditorNode;
